// docs.js — in-app user guide.
// Topics are markdown files under /docs/, listed in /docs/index.json.
// Loaded on-demand, cached, with full-text search across all topics.
// Glossary is generated dynamically from glossary.js.

import { GLOSSARY, searchGlossary } from './glossary.js';

const STATE = {
  index: null,
  topicCache: {},
  currentTopic: null,
};

const FALLBACK_INDEX = {
  topics: [
    { id: 'getting-started', title: 'Getting Started', section: 'Overview' },
  ]
};

async function loadIndex() {
  if (STATE.index) return STATE.index;
  try {
    const res = await fetch('/api/docs/guide', { headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('gdbs_token') || '') }, cache: 'no-store' });
    if (!res.ok) throw new Error('not 200');
    STATE.index = await res.json();
  } catch (_) {
    STATE.index = FALLBACK_INDEX;
  }
  return STATE.index;
}

async function loadTopic(id) {
  if (id === 'glossary') return { id, title: 'Physics Glossary', body: null };
  if (STATE.topicCache[id]) return STATE.topicCache[id];
  try {
    const res = await fetch(`/api/docs/guide/${encodeURIComponent(id)}`, { headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('gdbs_token') || '') }, cache: 'no-store' });
    if (!res.ok) throw new Error('not 200');
    const body = await res.text();
    STATE.topicCache[id] = { id, body };
    return STATE.topicCache[id];
  } catch (e) {
    return { id, body: `# ${id}\n\nThis topic isn't available yet.` };
  }
}

function renderMarkdown(md) {
  const escape = (s) => s.replace(/[&<>]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;' }[c]));
  let html = '';
  const lines = md.trim().split('\n');
  let inList = null; // 'ul' | 'ol' | null
  let inTable = false;
  let tableAlign = [];

  const closeList = () => { if (inList) { html += `</${inList}>`; inList = null; } };
  const closeTable = () => { if (inTable) { html += '</table>'; inTable = false; } };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (/^# /.test(line))      { closeList(); closeTable(); html += `<h1>${escape(line.slice(2))}</h1>`; continue; }
    if (/^## /.test(line))     { closeList(); closeTable(); html += `<h2>${escape(line.slice(3))}</h2>`; continue; }
    if (/^### /.test(line))    { closeList(); closeTable(); html += `<h3>${escape(line.slice(4))}</h3>`; continue; }
    if (/^#### /.test(line))   { closeList(); closeTable(); html += `<h4>${escape(line.slice(5))}</h4>`; continue; }

    // Match any line that starts with a pipe — the previous /^\| /
    // required a leading space, which dropped the alignment row
    // |---|---|---| out of the table parse and rendered it as a paragraph.
    if (/^\|/.test(line) && /\|/.test(line.slice(1))) {
      if (!inTable) { html += '<table>'; inTable = true; tableAlign = []; }
      // Strip outer pipes if present, then split — keeps both `| a | b |`
      // and `|---|---|` working symmetrically.
      const trimmed = line.replace(/^\|/, '').replace(/\|$/, '');
      const cells = trimmed.split('|').map(c => c.trim());
      if (cells.length > 0 && cells.every(c => /^:?-+:?$/.test(c))) {
        tableAlign = cells.map(c => c.startsWith(':') && c.endsWith(':') ? 'center'
                                 : c.endsWith(':') ? 'right' : 'left');
        continue;
      }
      const tag = tableAlign.length === 0 ? 'th' : 'td';
      html += '<tr>' + cells.map((c, idx) =>
        `<${tag} style="text-align:${tableAlign[idx] || 'left'};">${inlineMd(escape(c))}</${tag}>`
      ).join('') + '</tr>';
      continue;
    } else {
      closeTable();
    }

    if (/^```/.test(line)) {
      closeList();
      // Code block until next ```
      const code = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) { code.push(lines[i]); i++; }
      html += `<pre style="background:#0b1520;padding:12px;border-radius:4px;overflow-x:auto;border:1px solid #1e3a5f;"><code>${escape(code.join('\n'))}</code></pre>`;
      continue;
    }

    if (/^- /.test(line)) {
      if (inList !== 'ul') { closeList(); html += '<ul>'; inList = 'ul'; }
      html += `<li>${inlineMd(escape(line.slice(2)))}</li>`;
      continue;
    }
    if (/^\d+\. /.test(line)) {
      if (inList !== 'ol') { closeList(); html += '<ol>'; inList = 'ol'; }
      html += `<li>${inlineMd(escape(line.replace(/^\d+\. /, '')))}</li>`;
      continue;
    }
    if (line.trim() === '') { closeList(); continue; }

    closeList();
    html += `<p>${inlineMd(escape(line))}</p>`;
  }
  closeList(); closeTable();
  return html;
}

function inlineMd(s) {
  return s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, txt, url) => {
    if (url.startsWith('#docs:')) {
      const topic = url.replace('#docs:', '');
      return `<a href="javascript:void(0)" onclick="window.gdbsOpenDocs('${topic}')" style="color:#4fc3f7;">${txt}</a>`;
    }
    return `<a href="${url}" target="_blank" style="color:#4fc3f7;">${txt}</a>`;
  }).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code style="background:#0b1520;padding:1px 5px;border-radius:3px;color:#4fc3f7;">$1</code>');
}

function renderGlossaryView() {
  let html = `<h1>Physics Glossary</h1>
    <p>Every symbol used in the GDBS UI with definition and primary citation. Click any source to open the source paper.</p>
    <input id="docs-glossary-search" placeholder="Filter glossary..." style="width:100%;padding:8px;margin:10px 0;background:#051018;color:#e2e8f0;border:1px solid #1e3a5f;border-radius:4px;">
    <div id="docs-glossary-list">`;
  for (const [k, v] of Object.entries(GLOSSARY)) html += renderGlossaryEntry(k, v);
  return html + '</div>';
}

function renderGlossaryEntry(k, v) {
  return `
    <div class="docs-gloss-entry" data-k="${k}" style="padding:10px 12px;background:#0b1520;border:1px solid #1e3a5f;border-radius:4px;margin-bottom:8px;">
      <div style="color:#4fc3f7;font-weight:600;font-size:14px;">${v.symbol || ''} ${v.name || ''}</div>
      <div style="margin:4px 0;">${v.short || ''}</div>
      ${v.formula ? `<div style="color:#94a3b8;font-family:monospace;font-size:11px;">${v.formula}</div>` : ''}
      ${v.source ? `<div style="margin-top:4px;font-size:11px;"><a href="${v.url || '#'}" target="_blank" style="color:#4fc3f7;text-decoration:none;">${v.source}</a></div>` : ''}
    </div>`;
}

async function renderSidebar() {
  const idx = await loadIndex();
  const sections = {};
  for (const t of idx.topics) {
    (sections[t.section] = sections[t.section] || []).push(t);
  }
  sections['Glossary'] = [{ id: 'glossary', title: 'Physics Glossary' }];

  let html = '';
  for (const [sect, list] of Object.entries(sections)) {
    html += `<div class="docs-section-title">${sect}</div>`;
    for (const t of list) {
      html += `<div class="docs-nav-item" data-topic="${t.id}">${t.title}</div>`;
    }
  }
  return html;
}

export async function openDocsTopic(topicId) {
  const view = document.getElementById('view-docs') || await makeDocsView();
  document.querySelectorAll('.view').forEach(v => v.classList.remove('active'));
  view.classList.add('active');
  document.querySelectorAll('.nav-item').forEach(n => n.classList.remove('active'));

  STATE.currentTopic = topicId;
  const content = document.getElementById('docs-content');
  content.innerHTML = '<p style="color:#94a3b8;">Loading...</p>';

  if (topicId === 'glossary') {
    content.innerHTML = renderGlossaryView();
    wireGlossarySearch();
  } else {
    const topic = await loadTopic(topicId);
    content.innerHTML = renderMarkdown(topic.body) + renderDocFooter(topicId);
    wireDocFooter(topicId);
  }

  document.querySelectorAll('.docs-nav-item').forEach(n => {
    n.classList.toggle('active', n.dataset.topic === topicId);
  });
}

function renderDocFooter(topicId) {
  const moduleHint = deriveModuleFromTopic(topicId);
  return `
    <div class="docs-footer-card" style="margin-top:32px;padding:14px 16px;background:var(--bg-alt);border:1px solid var(--border);border-radius:6px;display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;">
      <div>
        <div style="color:var(--text);font-size:13px;font-weight:600;">Need help with this?</div>
        <div style="color:var(--text-dim);font-size:11px;margin-top:2px;">Open a support ticket — module and topic are pre-filled.</div>
      </div>
      <button id="docs-ticket-btn" data-topic="${topicId}" data-module="${moduleHint}"
        style="border:1px solid var(--accent);background:color-mix(in oklch, var(--accent) 10%, transparent);color:var(--accent);padding:8px 16px;border-radius:4px;cursor:pointer;font-size:12px;font-weight:600;">
        <i class="fas fa-life-ring"></i> Open ticket
      </button>
    </div>`;
}

function deriveModuleFromTopic(topicId) {
  if (topicId.startsWith('plasma'))   return 'plasma';
  if (topicId.startsWith('quantum'))  return 'quantum';
  if (topicId.startsWith('fluids'))   return 'fluids';
  if (topicId.startsWith('materials'))return 'materials';
  if (topicId.startsWith('topology')) return 'topology';
  if (topicId.startsWith('geophysics'))return 'geophysics';
  if (topicId.startsWith('medical'))  return 'medical';
  if (topicId.startsWith('cosmic'))   return 'cosmic';
  if (topicId.startsWith('theory'))   return 'theory';
  if (topicId === 'tickets' || topicId === 'troubleshooting' || topicId === 'citing-gdbs') return 'general';
  return 'general';
}

function wireDocFooter(topicId) {
  const btn = document.getElementById('docs-ticket-btn');
  if (!btn) return;
  btn.addEventListener('click', () => {
    const topicNode = document.querySelector(`.docs-nav-item[data-topic="${topicId}"]`);
    const title = topicNode?.textContent?.trim() || topicId;
    window.gdbsOpenTicketWithContext?.({
      module: btn.dataset.module,
      category: 'question',
      priority: 'P3',
      subject: `Question about: ${title}`,
      body: `I'm reading the docs page "${title}" (/docs#${topicId}) and have a question about:\n\n[describe what you're trying to do or what's unclear]\n\n`,
      context: { docs_topic: topicId },
    });
  });
}
window.gdbsOpenDocs = openDocsTopic;

function wireGlossarySearch() {
  const search = document.getElementById('docs-glossary-search');
  const list = document.getElementById('docs-glossary-list');
  if (!search || !list) return;
  search.addEventListener('input', () => {
    const matches = searchGlossary(search.value);
    list.innerHTML = matches.map(([k, v]) => renderGlossaryEntry(k, v)).join('');
  });
}

async function makeDocsView() {
  const main = document.querySelector('main') || document.body;
  if (document.getElementById('view-docs')) return document.getElementById('view-docs');

  const v = document.createElement('div');
  v.id = 'view-docs';
  v.className = 'view';
  v.innerHTML = `
    <style>
      /* app.css sets .view.active { flex-direction: column }, which would
         stack the docs sidebar above the content. Force row + full height
         here and override the inherited column direction.
         All colors resolve through theme vars so the guides stay readable
         in both dark and light modes (previous hardcoded slate hex made
         body text invisible on white). */
      #view-docs.active { display: flex !important; flex-direction: column !important; gap: 0; height: 100%; overflow: hidden; }
      /* sidebar + content sit side by side in this row; the column parent leaves
         room below for the "Conforms to" footer (appended to #view-docs by
         conforms-to.js) so it lands full-width at the bottom instead of as a
         third column floating to the right. */
      .docs-body { display: flex; flex-direction: row; flex: 1 1 auto; min-height: 0; overflow: hidden; }
      #view-docs > .conforms-footer { flex: 0 0 auto; max-height: 42%; overflow-y: auto; }
      .docs-sidebar {
        flex: 0 0 240px;
        background: var(--bg-alt);
        border-right: 1px solid var(--border);
        padding: 16px 0; overflow-y: auto; min-height: 0;
      }
      .docs-section-title {
        padding: 14px 18px 6px;
        color: var(--text-dim);
        font-size: 10px; font-weight: 700;
        text-transform: uppercase; letter-spacing: 0.06em;
      }
      .docs-nav-item {
        display: block; padding: 8px 18px;
        color: var(--text-dim);
        cursor: pointer; border-left: 3px solid transparent; font-size: 13px;
        transition: background 120ms ease, color 120ms ease, border-left-color 120ms ease;
      }
      .docs-nav-item:hover {
        background: color-mix(in oklch, var(--accent) 8%, transparent);
        color: var(--text);
      }
      .docs-nav-item.active {
        background: color-mix(in oklch, var(--accent) 14%, transparent);
        color: var(--accent);
        border-left-color: var(--accent);
      }
      #docs-content {
        flex: 1 1 auto; min-width: 0; min-height: 0; overflow-y: auto;
        padding: 24px 40px;
        color: var(--text);
        line-height: 1.7;
        background: var(--bg);
      }
      #docs-content h1 {
        color: var(--text-bright, var(--text));
        border-bottom: 1px solid var(--border);
        padding-bottom: 8px;
      }
      #docs-content h2 { color: var(--accent); margin-top: 24px; }
      #docs-content h3 { color: var(--text-bright, var(--text)); margin-top: 18px; }
      #docs-content h4 { color: var(--text); margin-top: 14px; opacity: 0.85; }
      #docs-content ul, #docs-content ol { padding-left: 24px; }
      #docs-content li { margin: 4px 0; color: var(--text); }
      #docs-content p { margin: 8px 0; color: var(--text); }
      #docs-content strong { color: var(--text-bright, var(--text)); }
      #docs-content code {
        background: var(--bg-alt);
        color: var(--text);
        padding: 1px 5px; border-radius: 3px;
        font-family: var(--mono, monospace); font-size: 0.92em;
      }
      #docs-content a { color: var(--accent); }
      #docs-content table { border-collapse: collapse; margin: 12px 0; width: 100%; }
      #docs-content th {
        background: var(--bg-alt);
        color: var(--accent);
        padding: 8px 12px;
        border-bottom: 1px solid var(--border);
        text-align: left;
      }
      #docs-content td {
        padding: 6px 12px;
        border-bottom: 1px solid var(--border);
        color: var(--text);
      }
    </style>
    <div class="docs-body">
      <div class="docs-sidebar" id="docs-sidebar"></div>
      <div id="docs-content"></div>
    </div>`;
  main.appendChild(v);

  const sidebar = document.getElementById('docs-sidebar');
  sidebar.innerHTML = await renderSidebar();
  sidebar.addEventListener('click', e => {
    const item = e.target.closest('.docs-nav-item');
    if (item) openDocsTopic(item.dataset.topic);
  });
  return v;
}

// Bootstrap (idempotent)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => makeDocsView());
} else {
  makeDocsView();
}
